import React, { useEffect, useMemo, useState, useContext } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import { useTableSort } from '../components/UI/tableSort';
import ActionButton from '../components/UI/ActionButton';
import Pager from '../components/UI/Pager';

const PAGE_SIZE = 10;

const ManageLoanReasons = () => {
  const { user } = useContext(AuthContext);
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const canView = perms.includes('LoanReason.View') || perms.includes('ManageConfig');
  const canCreate = perms.includes('LoanReason.Create') || perms.includes('ManageConfig');
  const canEdit = perms.includes('LoanReason.Edit') || perms.includes('ManageConfig');
  const canToggle = perms.includes('LoanReason.Toggle') || perms.includes('ManageConfig');

  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', description: '', is_active: true });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchReasons = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loan-reasons?all=1'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setReasons(data);
      } catch (e) {
        setError(e?.response?.data?.error || 'Failed to load loan reasons');
      } finally {
        setLoading(false);
      }
    };
    if (canView) fetchReasons();
  }, []);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(apiUrl('/api/loan-reasons?all=1'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReasons(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load loan reasons');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !canEdit) { setError('You do not have permission to save changes.'); return; }
    try {
      if (editingId) {
        if (!canEdit) { setError('You do not have permission to edit.'); return; }
        await axios.put(apiUrl(`/api/loan-reasons/${editingId}`), form, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        if (!canCreate) { setError('You do not have permission to create.'); return; }
        await axios.post(apiUrl('/api/loan-reasons'), form, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setForm({ name: '', description: '', is_active: true });
      setEditingId(null);
      setShowForm(false);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save reason');
    }
  };

  const startAdd = () => {
    setEditingId(null);
    setForm({ name: '', description: '', is_active: true });
    setShowForm(true);
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({ name: r.name || '', description: r.description || '', is_active: !!r.is_active });
    setShowForm(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: '', description: '', is_active: true });
    setShowForm(false);
  };

  const toggleActive = async (id) => {
    if (!canToggle) { setError('You do not have permission to toggle.'); return; }
    try {
      await axios.patch(apiUrl(`/api/loan-reasons/${id}/toggle`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refresh();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to toggle');
    }
  };

  // Narrowing the list must not strand you on a page that no longer exists.
  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return reasons.filter((r) => {
      if (statusFilter === 'active' && !r.is_active) return false;
      if (statusFilter === 'inactive' && r.is_active) return false;
      if (!q) return true;
      return [r.id, r.name, r.description, r.is_active ? 'active yes' : 'inactive no']
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(q));
    });
  }, [reasons, searchTerm, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(currentPage, totalPages);
  // A click on a column sorts every reason, then the page is taken.
  const reasonSort = useTableSort(filtered);
  const paginated = reasonSort.rows.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  return (
    <div className="max-w-full min-h-screen">

      {error && (
        <div className="p-2 rounded-lg mb-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* Add/Edit form — popup dialog */}
      {showForm && (
        <FullScreenPanel
          isOpen={showForm}
          onClose={cancelEdit}
          title={editingId ? 'Edit loan reason' : 'Add loan reason'}
        >
          <form onSubmit={onSubmit} className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={`w-full ${form.name.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                placeholder="e.g., Education"
                required
                disabled={!canCreate && !editingId}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full"
                placeholder="Optional description"
                disabled={!canCreate && !editingId}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-800 dark:text-slate-200">Active</label>
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                disabled={!canCreate && !editingId}
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={cancelEdit} className="px-3 py-1 border border-gray-300 dark:border-slate-600 rounded text-gray-700 dark:text-slate-200">
                Cancel
              </button>
              <ActionButton type="submit" className="px-3 py-1 bg-blue-600 text-white rounded" disabled={!(editingId ? canEdit : canCreate)} onClick={(e) => {
                const formEl = e.currentTarget.form;
                if (formEl && !formEl.reportValidity()) { e.preventDefault(); return; }
                return onSubmit(e);
              }} busyLabel="Saving…">
                {editingId ? 'Update' : 'Add'}
              </ActionButton>
            </div>
          </form>
        </FullScreenPanel>
      )}

      <div className="p-3 rounded-lg bg-white dark:bg-slate-900 shadow-md">
        {/* Search and status on the left, the action and pager hard right. */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name, description or ID…"
            aria-label="Search loan reasons"
            className="toolbar-control flex-1 min-w-[180px]"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
            className="toolbar-control min-w-[140px]"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <div className="flex flex-wrap items-center gap-2 ml-auto justify-end">
            {canCreate && (
              <button
                type="button"
                onClick={startAdd}
                className="toolbar-control px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
              >
                + Add reason
              </button>
            )}
            <Pager page={pageSafe} pages={totalPages} onPage={setCurrentPage} />
          </div>
        </div>

        <p className="mb-3 text-sm text-gray-600 dark:text-slate-300">
          <span className="font-semibold text-gray-900 dark:text-slate-100">{filtered.length}</span>
          {` reason${filtered.length === 1 ? '' : 's'}`}
          {filtered.length !== reasons.length ? ` of ${reasons.length}` : ''}
        </p>

        {loading ? (
          <div className="text-gray-600 dark:text-slate-400">Loading reasons…</div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-600 dark:text-slate-400">
            {reasons.length === 0 ? 'No reasons found.' : 'No reason matches that search.'}
          </div>
        ) : (
          <>
            <div>
              <ResponsiveTable
                data={paginated}
                sort={reasonSort.sort}
                onSortChange={reasonSort.onSortChange}
                columns={[
                  { field: 'id', header: 'ID', formatter: (value) => (
                      <span className="text-gray-800 dark:text-slate-200">{value}</span>
                    ) },
                  { field: 'name', header: 'Name', formatter: (value) => (
                      <span className="font-medium text-gray-900 dark:text-slate-100">{value}</span>
                    ) },
                  { field: 'description', header: 'Description', formatter: (value) => (
                      <span className="text-gray-700 dark:text-slate-200">{value || '-'}</span>
                    ) },
                  { field: 'is_active', header: 'Active', formatter: (value) => (
                      <StatusBadge status={value ? 'Active' : 'Inactive'} label={value ? 'Yes' : 'No'} />
                    ) },
                ]}
                actions={[
                  {
                    label: 'Edit',
                    onClick: (r) => startEdit(r),
                    condition: () => canEdit,
                    className: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 transition-colors',
                  },
                  {
                    label: 'Disable',
                    onClick: (r) => toggleActive(r.id),
                    condition: (r) => canToggle && r.is_active,
                    className: 'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 transition-colors',
                  },
                  {
                    label: 'Enable',
                    onClick: (r) => toggleActive(r.id),
                    condition: (r) => canToggle && !r.is_active,
                    className: 'bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 transition-colors',
                  },
                ]}
                emptyMessage="No reasons found."
              />
            </div>
          </>
        )}
      </div>

      <div className="text-xs text-gray-500 dark:text-slate-400 mt-3">Manage the reasons clients select when applying for a loan.</div>
    </div>
  );
};

export default ManageLoanReasons;
